"use client";

import { KeyboardEvent, PropsWithChildren, useCallback, useRef, useState } from "react";
import { FormattedLinkButton } from "~/components/layout/button";
import { ArrowDirection } from "~/components/layout/link";
import { Paragraph4 } from "~/components/layout/paragraph";
import { IConceptPageHeroCard } from "~/scripts/preload/interfaces";

interface IFlipCardStatefulPortionProps {
	readonly card: IConceptPageHeroCard;
}
const FlipCardStatefulPortion: React.FC<PropsWithChildren<IFlipCardStatefulPortionProps>> = ({
	children,
	card,
}) => {
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const [showBack, setShowBack] = useState(false);
	const toggleShowBack = useCallback(() => {
		setShowBack(showBack => !showBack);
	}, [setShowBack]);
	const toggleShowBackForEnterOrSpace = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter" || event.key === " ") {
				setShowBack(!showBack);
				if (!showBack && closeButtonRef.current) {
					closeButtonRef.current.focus();
				}
				event.preventDefault();
			}
		},
		[showBack, closeButtonRef],
	);

	const cardBackInner = (
		<div
			className={`[grid-area:main] z-10 py-4 px-4 flex flex-col items-stretch gap-4 relative h-full w-full border-white border-2 outline-none bg-white backface-hidden -rotate-y-180`}
		>
			<div className="grid grid-cols-12">
				<div className="font-title font-semibold text-left text-lg col-span-11 text-[color:var(--foreground-accent-color)]">
					{card.title}
				</div>
				<div
					key={`close_hero_card_${card.id}`}
					id={`close_hero_card_${card.id}`}
					className="relative inline-block w-6 m-1 overflow-hidden"
				>
					<button
						tabIndex={showBack ? 0 : -1}
						onClick={toggleShowBack}
						onMouseDown={e => e.preventDefault()}
						className="outline-none hover:border-[color:var(--foreground-accent-color)] focus-within:border-[color:var(--foreground-accent-color)] hover:bg-[color:var(--hero-background-accent-color)] focus-within:bg-[color:var(--hero-background-accent-color)]"
						ref={closeButtonRef}
					>
						<CloseX className="fill-[color:var(--foreground-accent-color)]" />
					</button>
				</div>
			</div>
			{card.body ? (
				<Paragraph4>
					<div className="text-left">{card.body}</div>
				</Paragraph4>
			) : null}
			{card.link ? (
				<div className="flex flex-col items-center">
					<FormattedLinkButton
						href={card.link.url}
						arrow={ArrowDirection.right}
						title={card.link.tooltip}
					>
						{card.link.linkText}
					</FormattedLinkButton>
				</div>
			) : null}
		</div>
	);

	return (
		<div
			className={`moz-backface-hidden shadow-[#00000040] shadow-lg grid h-full w-full bg-transparent rounded-lg transform-style-preserve-3d group [grid-template-areas:'main']
			outline-none relative min-h-[250px] transition-shadow motion-safe:transition-all motion-safe:duration-500 duration-500 origin-center hover:shadow-gray-500 focus-within:shadow-gray-500 ${
				showBack ? "-rotate-y-180 cursor-auto" : "cursor-pointer"
			}`}
			onClick={showBack ? undefined : toggleShowBack}
			onKeyDown={showBack ? undefined : toggleShowBackForEnterOrSpace}
			role={showBack ? "button" : ""}
			tabIndex={0}
		>
			{cardBackInner}
			{children}
		</div>
	);
};
export default FlipCardStatefulPortion;

const CloseX: React.FC<{ className: string }> = props => (
	<svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 384 512" {...props}>
		<path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
	</svg>
);
